import { useCallback, useMemo } from "react";
import * as Yup from "yup";
import { ButtonProps } from "@mui/material/Button";
import { Dialog } from "../../components/Dialog";
import Stack from "@mui/material/Stack";
import { useStyles } from "./styles";
import { useFormik } from "formik";
import TextField from "@mui/material/TextField";
import { DatePicker } from "../../components/DatePicker";
import { Person } from "../../types/person";
import { Autocomplete } from "../../components/Autocomplete";
import { GENRE } from "../../utils/constants";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { useMutation } from "react-query";
import { newPersonService } from "../../services/person";
import { useToast } from "../../hooks/useToast";
import { format } from "date-fns";

type DialogAddPersonProps = {
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  nome: Yup.string().required("Nome é obrigatório"),
  documento: Yup.string().required("Documento é obrigatório"),
});

function ModalAddPessoa({ open, onClose }: DialogAddPersonProps) {
  const styles = useStyles();
  const toast = useToast();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const newPerson = useMutation(
    async (data: Person) => {
      const response = await newPersonService(data);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Cliente adicionado com sucesso!", { severity: "success" });
        handleClose();
      },
      onError: () => {
        toast.show("Erro ao adicionar o cliente!", { severity: "error" });
      },
    }
  );

  const handleSubmit = useCallback(
    async (data: Person) => {
      await newPerson.mutateAsync(data);
    },
    [newPerson]
  );

  const formik = useFormik<Person>({
    initialValues: {
      blacklist_email: false,
      blacklist_sms: false,
      data_nascimento: format(new Date(), "yyyy-MM-dd"),
    } as Person,
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        disabled: newPerson.isLoading,
        children: "Fechar",
        onClick: handleClose,
      },
      {
        disabled: newPerson.isLoading,
        variant: "contained",
        children: newPerson.isLoading ? "Enviando solicitação" : "Salvar",
        onClick: () => formik.handleSubmit(),
      },
    ];
  }, [handleClose, formik, newPerson.isLoading]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      title="Novo cliente"
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stack className={styles.rootContentRow} spacing={4}>
        <Stack spacing={2} direction="row">
          <TextField
            autoFocus
            label="Nome"
            fullWidth
            size="small"
            value={formik.values.nome}
            helperText={formik.errors.nome}
            error={Boolean(formik.errors.nome)}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("nome", value)
            }
          />
          <TextField
            label="Documento"
            fullWidth
            size="small"
            value={formik.values.documento}
            helperText={formik.errors.documento}
            error={Boolean(formik.errors.documento)}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("documento", value)
            }
          />
          <DatePicker
            fullWidth
            size="small"
            label="Data de nascimento"
            onChange={(date) => formik.setFieldValue("data_nascimento", date)}
            value={formik.values.data_nascimento}
          />
        </Stack>
        <TextField
          label="Logradouro"
          fullWidth
          size="small"
          value={formik.values.rua}
          onChange={({ target: { value } }) =>
            formik.setFieldValue("rua", value)
          }
        />
        <Stack spacing={2} direction="row">
          <TextField
            label="Bairro"
            fullWidth
            size="small"
            value={formik.values.bairro}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("bairro", value)
            }
          />
          <TextField
            label="Cidade"
            fullWidth
            size="small"
            value={formik.values.cidade}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("cidade", value)
            }
          />
          <TextField
            label="UF"
            fullWidth
            size="small"
            value={formik.values.uf}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("uf", value)
            }
          />
          <TextField
            label="CEP"
            fullWidth
            size="small"
            value={formik.values.cep}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("cep", value)
            }
          />
        </Stack>
        <TextField
          label="Complemento"
          fullWidth
          size="small"
          onChange={({ target: { value } }) =>
            formik.setFieldValue("complemento", value)
          }
        />
        <Stack spacing={2} direction="row">
          <Autocomplete
            fullWidth
            size="small"
            label="Gênero"
            options={GENRE}
            getOptionLabel={(option) => option.label}
            onChange={(_, value) => formik.setFieldValue("sexo", value)}
          />
          <TextField
            type="tel"
            label="Celular"
            fullWidth
            size="small"
            value={formik.values.celular1}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("celular1", value)
            }
          />
          <TextField
            type="tel"
            label="Telefone"
            fullWidth
            size="small"
            value={formik.values.telefone1}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("telefone1", value)
            }
          />
          <TextField
            type="email"
            label="Email"
            fullWidth
            size="small"
            value={formik.values.email1}
            onChange={({ target: { value } }) =>
              formik.setFieldValue("email1", value)
            }
          />
        </Stack>
        <Stack spacing={2} direction="row">
          <FormControlLabel
            onChange={(_, value) =>
              formik.setFieldValue("blacklist_email", value)
            }
            checked={!formik.values.blacklist_email}
            control={<Checkbox />}
            label="Deseja receber oferta via email ?"
          />
          <FormControlLabel
            onChange={(_, value) =>
              formik.setFieldValue("blacklist_sms", value)
            }
            checked={!formik.values.blacklist_sms}
            control={<Checkbox />}
            label="Deseja receber oferta via sms ?"
          />
        </Stack>
      </Stack>
    </Dialog>
  );
}

export default ModalAddPessoa;
